import React, { useState } from "react";
import SEOHead from "../../components/SEO/SEOHead";
import {
  generateBreadcrumbStructuredData,
  generateWebPageStructuredData,
} from "../../components/SEO/StructuredData";
import LegalPageLayout from "../../components/Legal/LegalPageLayout";

const DESCRIPTION =
  "How Bowling Tournament App collects, uses, and protects personal information, including cookies, analytics, and your privacy rights.";

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("overview");

  const lastUpdated = "October 31, 2026";
  const structuredData = generateWebPageStructuredData({
    name: "Privacy Policy",
    description: DESCRIPTION,
    url: "/privacy-policy",
    dateModified: "2026-10-31",
  });
  const breadcrumbData = generateBreadcrumbStructuredData([
    { label: "Home", path: "/" },
    { label: "Privacy Policy", path: "/privacy-policy" },
  ]);

  const sections = [
    { id: "overview", title: "Overview" },
    { id: "collection", title: "Data Collection" },
    { id: "captcha", title: "CAPTCHA & Security" },
    { id: "usage", title: "Data Usage" },
    { id: "sharing", title: "Data Sharing" },
    { id: "rights", title: "Your Rights" },
    { id: "cookies", title: "Cookies & Tracking" },
    { id: "security", title: "Security" },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <SEOHead
        title="Privacy Policy - Bowling Tournament App"
        description={DESCRIPTION}
        keywords={[
          "privacy policy",
          "data protection",
          "personal information",
          "data collection",
          "user privacy",
          "GDPR compliance",
          "data security",
          "bowling tournament app privacy",
        ]}
        canonicalUrl="/privacy-policy"
        noIndex={false}
        additionalMeta={[{ name: "last-modified", content: lastUpdated }]}
        structuredData={structuredData}
        additionalStructuredData={[breadcrumbData]}
      />
      <LegalPageLayout
        title="Privacy Policy"
        lastUpdated={lastUpdated}
        sections={sections}
        activeSection={activeSection}
        onSectionClick={scrollToSection}
      >
        <div className="space-y-12">
                {/* Overview Section */}
                <section id="overview">
                  <h2 className="heading-display text-xl mb-4">
                    Overview & Commitment
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p>
                      At Bowling Tournament App, we are committed to protecting
                      your privacy and ensuring the security of your personal
                      information. This Privacy Policy explains how we collect,
                      use, disclose, and safeguard your data when you use our
                      platform.
                    </p>
                    <p>
                      This policy applies to all users of our platform,
                      including tournament organizers, participants, and
                      visitors. By using our services, you consent to the data
                      practices described in this policy.
                    </p>
                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Your Privacy Matters:</strong> We are committed
                        to transparency and giving you control over your
                        personal information. This policy explains your rights
                        and our responsibilities.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Data Collection Section */}
                <section id="collection">
                  <h2 className="heading-display text-xl mb-4">
                    Information We Collect
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Personal Information
                    </h3>
                    <p className="mb-4">
                      We collect the following types of personal information
                      when you use our platform:
                    </p>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Account Information
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 mb-6">
                      <li>Full name and display name</li>
                      <li>Email address</li>
                      <li>Phone number (optional)</li>
                      <li>USBC ID (optional)</li>
                      <li>Account preferences</li>
                    </ul>
                    <p className="mb-6 text-sm text-brand-muted">
                      Bowling Tournament App is not affiliated with, endorsed
                      by, or an official product of the United States Bowling
                      Congress (USBC). Providing a USBC ID here does not mean
                      this app sanctions a tournament or certifies averages.
                      Tournament organizers remain solely responsible for USBC
                      rules and sanctioning when those rules apply.
                    </p>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Tournament Data
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 mb-6">
                      <li>Tournament registrations</li>
                      <li>Bowling scores and statistics</li>
                      <li>Team affiliations</li>
                      <li>Tournament preferences</li>
                      <li>Communication history</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Automatically Collected Data
                    </h3>
                    <p className="mb-4">
                      We automatically collect certain information when you use
                      our platform:
                    </p>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Technical Information
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 mb-6">
                      <li>IP address and location data</li>
                      <li>Browser type and version</li>
                      <li>Device information</li>
                      <li>Operating system</li>
                      <li>Screen resolution</li>
                    </ul>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Usage Analytics
                    </h4>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Pages visited and time spent</li>
                      <li>Features used</li>
                      <li>Error logs and performance data</li>
                      <li>Session recordings (with consent)</li>
                      <li>Heatmap data</li>
                    </ul>
                  </div>
                </section>

                {/* CAPTCHA & Security Section */}
                <section id="captcha">
                  <h2 className="heading-display text-xl mb-4">
                    CAPTCHA & Security Data
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      CAPTCHA & Security Services
                    </h3>
                    <p className="mb-4">
                      Our platform uses CAPTCHA technology to protect against
                      automated abuse and ensure human interaction. Here's how
                      CAPTCHA data is handled:
                    </p>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      CAPTCHA Data Collection:
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 mb-6">
                      <li>
                        Browser fingerprinting data (device characteristics,
                        screen resolution, etc.)
                      </li>
                      <li>
                        User interaction patterns (mouse movements, keyboard
                        input)
                      </li>
                      <li>Network information (IP address, connection type)</li>
                      <li>Verification tokens (temporary, encrypted)</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      CAPTCHA Data Processing
                    </h3>
                    <p className="mb-4">
                      CAPTCHA data is processed as follows:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Third-party processing:</strong> CAPTCHA
                        verification is handled by our security service
                        provider (including Cloudflare Turnstile).
                      </li>
                      <li>
                        <strong>Personal information may be included:</strong>{" "}
                        The data listed above can include personal information
                        under CCPA/CPRA. For example, an IP address may be
                        personal information. CAPTCHA is not limited to
                        non-personal technical data.
                      </li>
                      <li>
                        <strong>Temporary tokens:</strong> Verification tokens
                        are stored temporarily and deleted when no longer
                        needed for verification.
                      </li>
                      <li>
                        <strong>Security purpose:</strong> This data is used
                        for bot detection and security verification.
                      </li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Session Storage Usage
                    </h3>
                    <p className="mb-4">We use browser session storage for:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Redirect Management:</strong> Storing intended
                        destinations for post-login redirects
                      </li>
                      <li>
                        <strong>Form State:</strong> Temporarily preserving form
                        data during multi-step processes
                      </li>
                      <li>
                        <strong>User Preferences:</strong> Storing temporary UI
                        preferences and settings
                      </li>
                      <li>
                        <strong>Security Tokens:</strong> Temporary storage of
                        authentication and verification tokens
                      </li>
                    </ul>
                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Note:</strong> Session storage data is
                        automatically cleared when you close your browser and is
                        not shared with third parties.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Data Usage Section */}
                <section id="usage">
                  <h2 className="heading-display text-xl mb-4">
                    How We Use Your Information
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Primary Uses
                    </h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Register and manage tournament participants</li>
                      <li>Process tournament registrations and payments</li>
                      <li>Communicate tournament updates and notifications</li>
                      <li>Provide customer support and assistance</li>
                      <li>Verify user identity and prevent fraud</li>
                      <li>Generate tournament results and statistics</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Analytics & Improvement
                    </h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Analyze platform usage and performance</li>
                      <li>Identify and fix technical issues</li>
                      <li>Improve user experience and features</li>
                      <li>Optimize platform security</li>
                      <li>Conduct research and development</li>
                      <li>Measure marketing effectiveness</li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <h4 className="font-semibold text-brand-charcoal mb-2">
                        Legal Basis for Processing
                      </h4>
                      <p className="text-brand-charcoal">
                        We process your data based on: <strong>Consent</strong>{" "}
                        (for optional features),{" "}
                        <strong>Contract Performance</strong> (for service
                        delivery), <strong>Legitimate Interest</strong> (for
                        security and improvement), and{" "}
                        <strong>Legal Obligation</strong> (for compliance).
                      </p>
                    </div>
                  </div>
                </section>

                {/* Data Sharing Section */}
                <section id="sharing">
                  <h2 className="heading-display text-xl mb-4">
                    Data Sharing & Third Parties
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Sale and sharing under CCPA/CPRA
                    </h3>
                    <p className="mb-4">
                      We do not sell personal information for money. Under
                      CCPA/CPRA, “sell” and “share” can also cover disclosing
                      personal information for cross-context behavioral
                      advertising. When you accept analytics cookies, we use
                      Microsoft Clarity. Microsoft may use that data for
                      Microsoft Advertising. That disclosure can be a “share”
                      (and in some cases a “sale”) as those terms are defined
                      under CCPA/CPRA.
                    </p>
                    <p className="mb-4">
                      To opt out of that analytics and advertising-related
                      disclosure, choose Necessary Only or Reject All on the
                      cookie banner, or email
                      privacy@bowlingtournamentapp.com with a Do Not Sell or
                      Share request. You can also block cookies in your
                      browser.
                    </p>
                    {/* TODO(Joe): Confirm whether the app honors Global Privacy Control (GPC). Do not claim GPC support until verified. */}

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Service Providers
                    </h3>
                    <p className="mb-4">
                      We may share data with trusted third-party service
                      providers who assist us in operating our platform:
                    </p>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Essential Services
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 mb-6">
                      <li>Cloud hosting and database providers</li>
                      <li>Authentication and security services</li>
                      <li>
                        Stripe, when an organization turns on Card payments.
                        Stripe processes the card for that organization.
                      </li>
                      <li>
                        Stripe, when an organization subscribes to AI Assist
                        or Priority. We send the organization name and the
                        director’s email. Stripe bills that card for Bowling
                        Tournament App.
                      </li>
                      <li>
                        Content delivery and performance optimization services
                      </li>
                      <li>Email service providers</li>
                    </ul>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Analytics & Security
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 mb-6">
                      <li>Analytics and performance monitoring services</li>
                      <li>Security and fraud prevention services</li>
                      <li>Error tracking and diagnostic services</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Legal Requirements
                    </h3>
                    <p className="mb-4">
                      We may disclose your information when required by law or
                      to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Comply with legal processes or government requests
                      </li>
                      <li>Protect our rights, property, or safety</li>
                      <li>Prevent fraud or security threats</li>
                      <li>Enforce our Terms of Service</li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <h4 className="font-semibold text-brand-charcoal mb-2">
                        Data Protection Agreements
                      </h4>
                      <p className="text-brand-charcoal">
                        All third-party service providers are bound by data
                        protection agreements and are required to maintain the
                        confidentiality and security of your data.
                      </p>
                    </div>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Sharing with Tournament Organizers
                    </h3>
                    <p className="mb-4">
                      <strong>Important:</strong> When you sign up for a
                      tournament, your personal information (name, email, phone,
                      USBC ID, average, etc.) is shared with the tournament
                      organizer for event management purposes.
                    </p>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Platform Role
                    </h4>
                    <p className="mb-4">
                      The Bowling Tournament App platform acts as a registration
                      tool and data processor. We collect and share your data
                      with tournament organizers to facilitate tournament
                      registration and administration.
                    </p>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Organizer Responsibilities
                    </h4>
                    <p className="mb-4">
                      Once your data is shared with a tournament organizer:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        The tournament organizer becomes the "Data Controller"
                        for your information
                      </li>
                      <li>
                        The organizer is responsible for how they use your data
                        after receiving it
                      </li>
                      <li>
                        The organizer is responsible for data accuracy and
                        responding to correction requests
                      </li>
                      <li>
                        The platform is not responsible for how organizers use
                        your data
                      </li>
                    </ul>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Data Corrections
                    </h4>
                    <p className="mb-4">
                      If you need to correct information shared with a
                      tournament organizer:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        Contact the tournament organizer directly for
                        corrections
                      </li>
                      <li>
                        Organizers are responsible for maintaining accurate data
                      </li>
                      <li>
                        You can also update your information through the
                        platform if the tournament allows it
                      </li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <h4 className="font-semibold text-brand-charcoal mb-2">
                        Data Processing Agreement
                      </h4>
                      <p className="text-brand-charcoal">
                        Tournament organizers must accept our{" "}
                        <a
                          href="/data-processing-agreement"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        >
                          Data Processing Agreement (DPA)
                        </a>{" "}
                        which outlines their responsibilities as data
                        controllers. This agreement specifies permitted uses,
                        security requirements, and liability limitations.
                      </p>
                    </div>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Manually Entered Data
                    </h4>
                    <p className="mb-4">
                      Tournament organizers may manually enter personal
                      information for bowlers who did not directly use the
                      website:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>
                        Organizers are responsible for having authorization to
                        enter a bowler's personal information
                      </li>
                      <li>
                        The organizer is the "Data Controller" for such manually
                        entered data
                      </li>
                      <li>
                        The platform acts only as a "Data Processor" and
                        stores/processes the data solely for tournament
                        administration
                      </li>
                      <li>
                        The platform is not liable for unauthorized, inaccurate,
                        or unlawful data entered by organizers
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Your Rights Section */}
                <section id="rights">
                  <h2 className="heading-display text-xl mb-4">
                    Your Privacy Rights
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      GDPR & CCPA Compliance
                    </h3>
                    <p className="mb-4">
                      Depending on your location, you may have the following
                      rights regarding your personal data:
                    </p>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Access & Control
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 mb-6">
                      <li>Access your personal data</li>
                      <li>Correct inaccurate information</li>
                      <li>
                        Delete your account. We remove your login, email,
                        phone, and profile. Tournaments, scores, and results
                        stay. Your name can still show on Results.
                      </li>
                      <li>Export your data (portability)</li>
                    </ul>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Processing Control
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 mb-6">
                      <li>Object to data processing</li>
                      <li>Restrict processing</li>
                      <li>Withdraw consent</li>
                      <li>File complaints</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      How to Exercise Your Rights
                    </h3>
                    <p className="mb-4">
                      To exercise your privacy rights, you can:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Contact us:</strong> Email
                        privacy@bowlingtournamentapp.com for access,
                        correction, deletion, Do Not Sell or Share, and other
                        privacy requests
                      </li>
                      <li>
                        <strong>Cookie banner:</strong> Choose Necessary Only
                        or Reject All to keep analytics cookies (including
                        Microsoft Clarity) off
                      </li>
                      <li>
                        <strong>Account settings:</strong> Update your
                        information through your account dashboard
                      </li>
                      <li>
                        <strong>Data Export:</strong> Request a copy of your
                        data in a portable format
                      </li>
                      <li>
                        <strong>Account Deletion:</strong> Request deletion of
                        your account. We remove your login, email, phone, and
                        profile. Tournaments, scores, and results stay. Your
                        name can still show on Results.
                      </li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <h4 className="font-semibold text-brand-charcoal mb-2">
                        Response Time
                      </h4>
                      <p className="text-brand-charcoal">
                        We will respond to your privacy requests within 30 days.
                        For complex requests, we may extend this period by an
                        additional 60 days with notification.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Cookies & Tracking Section */}
                <section id="cookies">
                  <h2 className="heading-display text-xl mb-4">
                    Cookies & Tracking Technologies
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Types of Cookies We Use
                    </h3>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Essential Cookies
                    </h4>
                    <p className="text-sm text-brand-muted mb-2">
                      Required for basic functionality
                    </p>
                    <ul className="list-disc pl-6 space-y-1 mb-6 text-sm">
                      <li>Authentication tokens</li>
                      <li>Session management</li>
                      <li>Security verification</li>
                      <li>CAPTCHA functionality</li>
                    </ul>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Analytics Cookies
                    </h4>
                    <p className="text-sm text-brand-muted mb-2">
                      Help us improve our services
                    </p>
                    <ul className="list-disc pl-6 space-y-1 mb-6 text-sm">
                      <li>Usage analytics</li>
                      <li>Performance monitoring</li>
                      <li>Error tracking</li>
                      <li>User behavior insights</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Microsoft Clarity
                    </h3>
                    <p className="mb-4">
                      We use Microsoft Clarity to see how people use this
                      website. Clarity records sessions and builds heatmaps of
                      clicks and scrolling. We use that to improve the site.
                      Clarity uses cookies and similar tools. Microsoft may
                      collect this data and use it for Microsoft Advertising.
                    </p>
                    <p className="mb-4">
                      Clarity runs only after you choose Accept All on the
                      cookie banner. Necessary Only and Reject All leave it
                      off. You can also block cookies in your browser.
                    </p>
                    <p className="mb-4">
                      How Microsoft collects and uses this data is in the{" "}
                      <a
                        href="https://privacy.microsoft.com/en-us/privacystatement"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-gold-ink hover:text-brand-charcoal underline"
                      >
                        Microsoft Privacy Statement
                      </a>
                      .
                    </p>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <h4 className="font-semibold text-brand-charcoal mb-2">
                        Cookie management
                      </h4>
                      <p className="text-brand-charcoal">
                        You can control cookies through the cookie banner on
                        this site and through your browser settings. Disabling
                        essential cookies may affect how the platform works.
                        Cookie and analytics practices are described in this
                        Privacy Policy section.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Security Section */}
                <section id="security">
                  <h2 className="heading-display text-xl mb-4">
                    Data Security & Breach Response
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Security Measures
                    </h3>
                    <p className="mb-4">
                      We take steps to protect your data in transit and limit
                      access to systems that store it:
                    </p>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Technical Security
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 mb-6">
                      <li>
                        Data in transit is protected with TLS/SSL encryption
                      </li>
                      <li>Secure data centers</li>
                      <li>Regular security reviews</li>
                      <li>Vulnerability scanning</li>
                    </ul>

                    <h4 className="font-display font-bold text-brand-charcoal mb-2">
                      Access Controls
                    </h4>
                    <ul className="list-disc pl-6 space-y-1 mb-6">
                      <li>Role-based access control</li>
                      <li>Regular access reviews</li>
                      <li>Secure API endpoints</li>
                    </ul>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Data Breach Response
                    </h3>
                    <p className="mb-4">
                      In the event of a data breach, we will:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Immediately investigate and contain the breach</li>
                      <li>
                        Notify affected users within 72 hours (where required by
                        law)
                      </li>
                      <li>Report to relevant authorities as required</li>
                      <li>
                        Implement additional security measures to prevent future
                        breaches
                      </li>
                      <li>Provide guidance and support to affected users</li>
                    </ul>

                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <h4 className="font-semibold text-brand-charcoal mb-2">
                        Your Role in Security
                      </h4>
                      <p className="text-brand-charcoal">
                        You can help protect your data by using strong
                        passwords, keeping your devices secure, and being
                        cautious about sharing personal information.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Contact & Updates Section */}
                <section className="border-t border-brand-charcoal/10 pt-8">
                  <h2 className="heading-display text-xl mb-4">
                    Contact Us & Policy Updates
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Questions About Privacy?
                    </h3>
                    <p className="mb-4">
                      If you have questions about this Privacy Policy or your
                      data, please contact us:
                    </p>
                    <p className="font-medium">
                      Email: privacy@bowlingtournamentapp.com
                    </p>
                    <p className="text-sm text-brand-muted">
                      We aim to respond as soon as reasonably practicable.
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Policy Updates
                    </h3>
                    <p>
                      We may update this Privacy Policy to reflect changes in
                      our practices or legal requirements. Material changes are
                      posted on this page with an updated “Last Updated” date.
                      Continued use after the effective date means you accept
                      the updated policy. If you disagree, stop using the
                      service and close your account under the account deletion
                      process described above.
                    </p>
                    <p className="font-medium mt-4">
                      Last Updated: {lastUpdated}
                    </p>
                  </div>
                </section>
        </div>
      </LegalPageLayout>
    </>
  );
};

export default PrivacyPolicy;
